import { useEffect, useMemo, useState } from "react";
import { Search, Zap, ShieldCheck, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/store/ProductCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/5304d42c-fd35-4a9b-902b-c4d84626773d/images/4cda93ff18b8fe09e87294fb431c88581a8016712e494fb4fb524065f2f9dbbb.jpeg";

const Hero = ({ s }) => (
  <section className="relative overflow-hidden noise">
    <div className="absolute inset-0 grid-bg" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-16 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 relative rounded-3xl overflow-hidden min-h-[340px] border border-white/10 rise">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/85 to-transparent" />
        <div className="relative p-8 sm:p-12 max-w-xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-cyan-300">Itens de jogo</p>
          <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]" data-testid="hero-title">
            {s?.hero_title || "Itens de jogo com entrega rápida"}
          </h1>
          <p className="text-base md:text-lg text-slate-300 mt-5" data-testid="hero-subtitle">{s?.hero_subtitle}</p>
          <a href="#catalogo" className="inline-flex mt-8 h-12 px-7 rounded-full bg-violet-600 hover:bg-violet-500 transition-colors items-center font-semibold" data-testid="hero-cta-button">Ver catálogo</a>
          {s?.server_address && (
            <button onClick={() => { navigator.clipboard.writeText(s.server_address); toast.success("IP copiado!"); }} data-testid="hero-server-ip-button"
              className="inline-flex mt-8 ml-3 h-12 px-5 rounded-full border border-emerald-500/40 bg-[#0B0F19]/70 text-emerald-300 items-center gap-2 font-mono text-sm hover:bg-emerald-500/10">
              <Copy className="w-4 h-4" /> {s.server_address}
            </button>
          )}
        </div>
      </div>
      <div className="lg:col-span-4 grid gap-4">
        {[
          { icon: Zap, t: "Entrega rápida", d: "Recebe no seu nick após a confirmação", c: "text-amber-300" },
          { icon: ShieldCheck, t: "Pix ou MB Way", d: "Pague em reais (Brasil) ou euros (Portugal)", c: "text-emerald-300" },
          { icon: Sparkles, t: "Itens raros", d: "Skins, VIPs, moedas e contas", c: "text-violet-300" },
        ].map((x, i) => (
          <div key={x.t} className="rise rounded-2xl bg-[#111827]/90 border border-white/10 p-5 flex gap-4" style={{ animationDelay: `${120 + i * 90}ms` }}>
            <x.icon className={`w-6 h-6 shrink-0 ${x.c}`} />
            <div><p className="font-heading font-bold">{x.t}</p><p className="text-sm text-slate-400 mt-1">{x.d}</p></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const { settings } = useStore();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState(null);
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    http.get("/categories").then((r) => setCats(r.data));
    http.get("/products").then((r) => setProducts(r.data));
  }, []);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const list = (products || []).filter((p) => (active === "all" || p.category_id === active) && p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Hero s={settings} />
      <section id="catalogo" className="max-w-7xl mx-auto px-5 sm:px-8 scroll-mt-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-500">Catálogo</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-2">Escolha o seu item</h2>
          </div>
          <div className="relative md:w-72">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar item..." data-testid="store-search-input"
              className="w-full h-11 pl-11 pr-4 rounded-full bg-white/5 border border-white/10 focus:border-violet-500 outline-none text-sm" />
          </div>
        </div>
        <div className="flex gap-2 mt-8 overflow-x-auto pb-2" data-testid="category-filters">
          {[{ id: "all", name: "Todos", color: "#7C3AED" }, ...cats].map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`category-filter-${c.id}`}
              className={`shrink-0 h-10 px-5 rounded-full text-sm font-medium border transition-colors ${active === c.id ? "text-white border-transparent" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
              style={active === c.id ? { background: c.color } : {}}>
              {c.name}
            </button>
          ))}
        </div>
        {products === null ? (
          <p className="text-slate-500 mt-10" data-testid="products-loading">Carregando itens...</p>
        ) : list.length === 0 ? (
          <p className="text-slate-500 mt-10" data-testid="products-empty">Nenhum item encontrado.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8" data-testid="product-grid">
            {list.map((p, i) => <ProductCard key={p.id} p={p} cat={catMap[p.category_id]} i={i} />)}
          </div>
        )}
      </section>
    </>
  );
}
